// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { hiraganas } from "../../consts/hiraganaList";
import { katakana } from "../../consts/katakana";
import { buildRomajiMap, convertRomajiSentence } from "../../utils/romajiConverter";
import { getWordCorrections } from "../../utils/correctionEngine";
import Seo from "../Seo/SeoComponent";
import * as analytics from "../../utils/analytics";
import styles from "./romajiConverterPage.module.scss";
import PageLanguageLink from "../PageLanguageLink/PageLanguageLink";

const examplesByMode = {
    hiragana: [
        { label: "konichiwa", romaji: "konichiwa" },
        { label: "ohayo", romaji: "ohayo" },
        { label: "ogenki desuka?", romaji: "ogenki desuka?" }
    ],
    katakana: [
        { label: "gaming", romaji: "geemingu" },
        { label: "gardening", romaji: "gaadeningu" }
    ]
};

function RomajiConverterPage() {
    const { t, i18n } = useTranslation("romajiConverterPage");
    const [mode, setMode] = useState("hiragana");
    const [input, setInput] = useState("");
    const [copyError, setCopyError] = useState(false);
    const [completedInput, setCompletedInput] = useState("");
    const [dismissedCorrection, setDismissedCorrection] = useState("");

    const isPt = i18n.language === "pt";
    const switchPath = isPt ? "/romaji-converter" : "/pt/romaji-converter";
    const currentPath = isPt ? "/pt/romaji-converter" : "/romaji-converter";

    const romajiMap = useMemo(
        () => buildRomajiMap(mode === "hiragana" ? hiraganas : katakana),
        [mode]
    );

    const outputSegments = useMemo(
        () => convertRomajiSentence(input, romajiMap, mode === "katakana"),
        [input, romajiMap, mode]
    );

    const output = useMemo(
        () => outputSegments.map((segment) => segment.text).join(""),
        [outputSegments]
    );

    const hasUnconverted = outputSegments.some((segment) => !segment.converted);
    useEffect(() => {
        setDismissedCorrection("");
        const timeout = setTimeout(() => {
            setCompletedInput(input);
            if (input.trim()) {
                analytics.track("converter_used", {
                    mode: `romaji_to_${mode}`,
                    inputLength: input.trim().length,
                    found: !hasUnconverted,
                });
            }
        }, 500);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input]);

    const corrections = useMemo(() => {
        if (mode !== "hiragana" || !completedInput.trim() || dismissedCorrection === completedInput) return [];
        return getWordCorrections(completedInput, romajiMap);
    }, [completedInput, dismissedCorrection, mode, romajiMap]);

    const examples = examplesByMode[mode];
    const modeLabel = t(mode);

    function handleModeChange(nextMode) {
        setMode(nextMode);
        setCompletedInput("");
        setDismissedCorrection("");
    }

    function handleExampleClick(romaji) {
        setInput(romaji);
    }

    function handleClear() {
        setInput("");
    }

    function handleCopy() {
        if (!output) return;
        navigator.clipboard
            .writeText(output)
            .then(() => setCopyError(false))
            .catch(() => setCopyError(true));
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />

            <main className={styles.main}>
                <section className={styles.pageHeader}>
                    <div className={styles.pageHeaderIntro}>
                        <h1 className={styles.pageTitle}>{t("title")}</h1>
                        <p className={styles.pageDescription}>{t("description")}</p>
                    </div>

                    {mode === "katakana" && (
                        <div className={styles.pageInfo}>{t("katakanaInfo")}</div>
                    )}
                </section>

                <div className={styles.innerGrid}>
                    <section className={styles.panelCard}>
                        <div className={styles.panelHeader}>
                            <p className={styles.panelTag}>{t("input")}</p>
                            <div className={styles.modeTabs} role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={mode === "hiragana"}
                                    className={`${styles.modeTab} ${mode === "hiragana" ? styles.modeTabActive : ""}`}
                                    onClick={() => handleModeChange("hiragana")}
                                >
                                    {t("hiragana")}
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={mode === "katakana"}
                                    className={`${styles.modeTab} ${mode === "katakana" ? styles.modeTabActive : ""}`}
                                    onClick={() => handleModeChange("katakana")}
                                >
                                    {t("katakana")}
                                </button>
                            </div>
                        </div>

                        <label htmlFor="romaji-converter-input" className={styles.srOnly}>
                            {t("romajiInput")}
                        </label>
                        <textarea
                            id="romaji-converter-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={t("placeholder")}
                            className={styles.textarea}
                            rows={5}
                        />

                        <div className={styles.examplesSection}>
                            <div className={styles.examplesHeader}>
                                <span>{t("examples")}</span>
                                <span className={styles.examplesNote}>{t("tapToFill")}</span>
                            </div>
                            <div className={styles.examplesRow}>
                                {examples.map((example) => (
                                    <button
                                        key={example.label}
                                        type="button"
                                        className={styles.exampleButton}
                                        onClick={() => handleExampleClick(example.romaji)}
                                    >
                                        {example.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={styles.actionRow}>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.secondaryButton}`}
                                onClick={handleClear}
                                disabled={!input}
                            >
                                {t("clear")}
                            </button>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.primaryButton}`}
                                onClick={handleCopy}
                                disabled={!output}
                            >
                                {t("copy")}
                            </button>
                        </div>
                    </section>

                    <section className={styles.panelCard}>
                        <div className={styles.resultHeader}>
                            <div>
                                <p className={styles.panelTag}>{t("result")}</p>
                                <h3 className={styles.sectionTitle}>{t("outputTitle", { mode: modeLabel })}</h3>
                            </div>
                            {output && (
                                <button
                                    type="button"
                                    className={`${styles.actionButton} ${styles.secondaryButton}`}
                                    onClick={handleCopy}
                                >
                                    {t("copy")}
                                </button>
                            )}
                        </div>

                        {output ? (
                            <>
                                <div className={styles.resultBox}>
                                    {outputSegments.map((segment, index) => (
                                        <span
                                            key={index}
                                            className={segment.converted ? undefined : styles.unconverted}
                                        >
                                            {segment.text}
                                        </span>
                                    ))}
                                </div>
                                {hasUnconverted && (
                                    <p className={styles.unconvertedHint}>
                                        {t("unconvertedHint", { mode: mode })}
                                    </p>
                                )}
                                {corrections.map((correction) => (
                                    <div key={correction.correct} className={styles.correctionHint}>
                                        <p>
                                            {t("didYouMean")}{" "}
                                            <span className={styles.correctionKana}>{correction.correct}</span>
                                            {" — "}{correction.reading}{" — "}{correction.romaji}{" — "}
                                            {correction.meaning.toLowerCase()}
                                        </p>
                                        <button
                                            type="button"
                                            className={styles.dismissCorrection}
                                            onClick={() => setDismissedCorrection(completedInput)}
                                        >
                                            {t("dismiss")}
                                        </button>
                                    </div>
                                ))}
                                {copyError && (
                                    <p className={styles.copyErrorText} role="alert">
                                        {t("copyError")}
                                    </p>
                                )}
                            </>
                        ) : (
                            <div className={styles.emptyState}>
                                <p>{t("emptyState", { mode: mode })}</p>
                            </div>
                        )}
                    </section>
                </div>

                <PageLanguageLink to={switchPath} />
            </main>

            <Footer />
        </div>
    );
}

export default RomajiConverterPage;
